

import { useState, useCallback, useEffect } from "react";
import { toast } from "../../../lib/toast";
import * as participanteService from "../../../services/participante.service";
import type { Participante } from "../../../services/participante.service";
import { LIMITS, ERROR_MESSAGES } from "../../../lib/constants";

interface UseParticipantsParams {
  ataId: string;
  initialParticipantes: Participante[];
  onUpdate: () => Promise<void>;
  onParticipanteAdicionado?: () => void;
  onChangeDetected?: () => void;
}

interface UseParticipantsReturn {
  participantes: Participante[];
  showParticipantModal: boolean;
  editingParticipant: Participante | null;
  participantForm: {
    nome: string;
    empresa: string;
    cargo: string;
    email: string;
  };
  setShowParticipantModal: (show: boolean) => void;
  setEditingParticipant: (participant: Participante | null) => void;
  setParticipantForm: (form: Partial<{ nome: string; empresa: string; cargo: string; email: string }>) => void;
  resetParticipantForm: () => void;
  handleCreateParticipant: () => Promise<void>;
  handleUpdateParticipant: () => Promise<void>;
  handleDeleteParticipant: (participantId: string) => Promise<void>;
  handleBulkAddParticipants: (nomes: string[]) => Promise<void>;
  handleAddWithDetails: (data: { nome: string; empresa?: string; cargo?: string; email?: string }) => Promise<void>;
}

const initialForm = {
  nome: "",
  empresa: "",
  cargo: "",
  email: "",
};

export function useParticipants({
  ataId,
  initialParticipantes,
  onUpdate,
  onParticipanteAdicionado,
  onChangeDetected,
}: UseParticipantsParams): UseParticipantsReturn {
  const [participantes, setParticipantes] = useState<Participante[]>(
    initialParticipantes
  );
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [editingParticipant, setEditingParticipant] =
    useState<Participante | null>(null);
  const [participantForm, setParticipantFormState] = useState(initialForm);


  useEffect(() => {
    setParticipantes(initialParticipantes);
  }, [initialParticipantes]);

  const setParticipantForm = useCallback(
    (form: Partial<typeof initialForm>) => {
      setParticipantFormState((prev) => ({ ...prev, ...form }));
    },
    []
  );

  const resetParticipantForm = useCallback(() => {
    setParticipantFormState(initialForm);
    setEditingParticipant(null);
  }, []);

  const handleCreateParticipant = useCallback(async () => {
    if (!participantForm.nome.trim()) {
      toast.error("Nome do participante é obrigatório. Informe o nome completo.");
      return;
    }

    if (participantForm.nome.trim().length > LIMITS.PARTICIPANT_NAME_MAX) {
      toast.error(`Nome excedeu o limite de ${LIMITS.PARTICIPANT_NAME_MAX} caracteres.`);
      return;
    }

    // Verificar limite de participantes
    if (participantes.length >= LIMITS.MAX_PARTICIPANTS_PER_MEETING) {
      toast.error(ERROR_MESSAGES.MAX_PARTICIPANTS_REACHED);
      return;
    }

    try {
      const novoParticipante = await participanteService.createParticipante(
        ataId,
        {
          nome: participantForm.nome.trim(),
          empresa: participantForm.empresa.trim() || undefined,
          cargo: participantForm.cargo.trim() || undefined,
          email: participantForm.email.trim() || undefined,
        }
      );

      setParticipantes([...participantes, novoParticipante]);
      resetParticipantForm();
      setShowParticipantModal(false);
      toast.success("Participante adicionado com sucesso.");
      

      if (onParticipanteAdicionado) {
        onParticipanteAdicionado();
      }
    } catch (error: any) {
      const errMsg = error.message || '';
      if (errMsg.includes('conexão') || errMsg.includes('internet')) {
        toast.error('Não foi possível adicionar o participante — verifique sua conexão.');
      } else {
        toast.error(errMsg || 'Não foi possível adicionar o participante. Tente novamente.');
      }
    }
  }, [ataId, participantForm, participantes, resetParticipantForm, onParticipanteAdicionado]);

  const handleUpdateParticipant = useCallback(async () => {
    if (!editingParticipant || !participantForm.nome.trim()) {
      toast.error("Nome do participante é obrigatório. Informe o nome completo.");
      return;
    }

    if (participantForm.nome.trim().length > LIMITS.PARTICIPANT_NAME_MAX) {
      toast.error(`Nome excedeu o limite de ${LIMITS.PARTICIPANT_NAME_MAX} caracteres.`);
      return;
    }

    try {
      const updated = await participanteService.updateParticipante(
        editingParticipant.id,
        {
          nome: participantForm.nome.trim(),
          empresa: participantForm.empresa.trim() || undefined,
          cargo: participantForm.cargo.trim() || undefined,
          email: participantForm.email.trim() || undefined,
        }
      );

      setParticipantes(
        participantes.map((p) => (p.id === updated.id ? updated : p))
      );
      resetParticipantForm();
      setShowParticipantModal(false);
      toast.success("Participante atualizado com sucesso.");
    } catch (error: any) {
      toast.error(error.message || 'Não foi possível atualizar o participante. Tente novamente.');
    }
  }, [
    editingParticipant,
    participantForm,
    participantes,
    resetParticipantForm,
  ]);

  const handleDeleteParticipant = useCallback(
    async (participantId: string) => {
      try {
        await participanteService.deleteParticipante(participantId);
        setParticipantes(participantes.filter((p) => p.id !== participantId));
        toast.success("Participante excluído com sucesso.");
        onChangeDetected?.();
      } catch (error: any) {
        toast.error(error.message || 'Não foi possível excluir o participante. Tente novamente.');
      }
    },
    [participantes, onChangeDetected]
  );

  const handleBulkAddParticipants = useCallback(
    async (nomes: string[]) => {
      if (nomes.length === 0) return;

      // Verificar limite de participantes
      if (participantes.length + nomes.length > LIMITS.MAX_PARTICIPANTS_PER_MEETING) {
        const remaining = LIMITS.MAX_PARTICIPANTS_PER_MEETING - participantes.length;
        if (remaining <= 0) {
          toast.error(ERROR_MESSAGES.MAX_PARTICIPANTS_REACHED);
          return;
        }
        toast.warning(`Só é possível adicionar mais ${remaining} participante(s). Limite de ${LIMITS.MAX_PARTICIPANTS_PER_MEETING} por ata.`);
        nomes = nomes.slice(0, remaining);
      }

      try {
        const newParticipants: Participante[] = [];

        // Create participants sequentially to avoid race conditions
        for (const nome of nomes) {
          const novoParticipante = await participanteService.createParticipante(
            ataId,
            { nome: nome.trim() }
          );
          newParticipants.push(novoParticipante);
        }

        setParticipantes((prev) => [...prev, ...newParticipants]);
        toast.success(`${nomes.length} participante${nomes.length > 1 ? 's' : ''} adicionado${nomes.length > 1 ? 's' : ''} com sucesso.`);

        onParticipanteAdicionado?.();
        onChangeDetected?.();
      } catch (error: any) {
        toast.error(error.message || 'Não foi possível adicionar os participantes. Tente novamente.');
      }
    },
    [ataId, onParticipanteAdicionado, onChangeDetected, participantes.length]
  );

  // Add participant with full details (used for adding collaborators and contacts)
  const handleAddWithDetails = useCallback(
    async (data: { nome: string; empresa?: string; cargo?: string; email?: string }) => {
      if (!data.nome.trim()) {
        toast.error("Nome do participante é obrigatório. Informe o nome completo.");
        return;
      }

      // Verificar limite
      if (participantes.length >= LIMITS.MAX_PARTICIPANTS_PER_MEETING) {
        toast.error(ERROR_MESSAGES.MAX_PARTICIPANTS_REACHED);
        return;
      }

      try {
        const novoParticipante = await participanteService.createParticipante(
          ataId,
          {
            nome: data.nome.trim(),
            empresa: data.empresa?.trim() || undefined,
            cargo: data.cargo?.trim() || undefined,
            email: data.email?.trim() || undefined,
          }
        );

        setParticipantes((prev) => [...prev, novoParticipante]);
        toast.success("Participante adicionado com sucesso.");

        onParticipanteAdicionado?.();
        onChangeDetected?.();
      } catch (error: any) {
        toast.error(error.message || 'Não foi possível adicionar o participante. Tente novamente.');
      }
    },
    [ataId, onParticipanteAdicionado, onChangeDetected, participantes.length]
  );

  return {
    participantes,
    showParticipantModal,
    editingParticipant,
    participantForm,
    setShowParticipantModal,
    setEditingParticipant: (participant) => {
      setEditingParticipant(participant);
      if (participant) {
        setParticipantForm({
          nome: participant.nome,
          empresa: participant.empresa || "",
          cargo: participant.cargo || "",
          email: participant.email || "",
        });
      } else {
        resetParticipantForm();
      }
    },
    setParticipantForm,
    resetParticipantForm,
    handleCreateParticipant,
    handleUpdateParticipant,
    handleDeleteParticipant,
    handleBulkAddParticipants,
    handleAddWithDetails,
  };
}

